import { DatePipe } from "@angular/common";
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Question } from "src/app/services/settings/forms.service";
import {
    Status,
    QRCodeService,
    QRCodeSetup,
    Options,
    QRCodeRoleAccessType
} from "src/app/services/settings/qrcode.service";
import { AsyncModalService } from "src/app/services/shared/async-modal-service";
import { DateTimeService } from "src/app/services/shared/datetime.service";
import { ButtonOption } from "../../../components/datatable/datatablecolumn/datatablecolumn.component";
import { QRCodeForm } from "./qrcode.form.component";

declare var bootstrap: any;

@Component({
    standalone: false,
    selector: "app-qrcode-register",
    templateUrl: "./qrcode.register.component.html",
    styleUrls: ["./qrcode.register.component.css"]
})
export class QRCodeRegister {
    @ViewChild("qrCodeSetupButton") private qrCodeSetupButton: ElementRef;
    @ViewChild("qrCodeForm") private qrCodeForm: QRCodeForm;
    
    @Input() qrCodeFormQuestions: Question[];
    @Input("isDisplayEntry") isDisplayEntry: boolean;
    @Input("isAllowUploadedCSV") isAllowUploadedCSV: boolean;
    @Input("qrCodeSetupRegister") qrCodeSetupRegister: any[];
    @Output() onQRCodeSetupRegisterChange = new EventEmitter<any>();

    showQRCodeSetupModal: boolean
    qrCodeName: string;
    qrCodeSetup: QRCodeSetup;
    isViewMode: boolean;
    isEditMode: boolean;
    loading: boolean;
    formId: number;

    constructor(
        private route: ActivatedRoute,
        private asyncModalService: AsyncModalService,
        private qrCodeService: QRCodeService
    ) {
    }

    //#region hook methods

    ngOnInit() {
        this.formId = 0;
        this.qrCodeSetup = new QRCodeSetup;
        this.isViewMode = false;
        this.isEditMode = false;
        this.showQRCodeSetupModal = false;
        //set formid
        let params = this.route.snapshot.params;
        if (params["id"]) {
            this.formId = +params["id"];
        }

        if (this.qrCodeSetupRegister.length > 0) {
            this.setQRCodeSetupRegister();
        }
    }

    //#endregion

    onSaveQRCodeSetting() {
        this.refreshTable();
    }

    onCloseQRCodeSetting(event) {
        this.showQRCodeSetupModal = false;

        // Bootstrap 5's own close cleanup (hiding the element, removing the
        // backdrop, resetting body state) waits on a CSS transitionend event
        // that doesn't fire reliably once Angular's *ngIf tears down the
        // modal's content around the same time - a Bootstrap 5 behavior
        // change (Bootstrap 4, used before the upgrade, needed none of this;
        // same root cause as the Label Preparation modal). Rather than race
        // that unreliable event with a timer, the close button no longer
        // carries data-bs-dismiss - this handler is now the sole, synchronous
        // owner of closing the modal: it tears everything down directly and
        // disposes the Modal instance immediately, so there's nothing left
        // pending and no delay of any kind is needed.
        const modalElement = document.getElementById("add-qr-code-setup");

        if (modalElement) {
            modalElement.classList.remove("show");
            modalElement.style.display = "none";
            modalElement.setAttribute("aria-hidden", "true");
            modalElement.removeAttribute("aria-modal");
            modalElement.removeAttribute("role");

            if (typeof bootstrap !== "undefined") {
                bootstrap.Modal.getInstance(modalElement)?.dispose();
            }
        }

        if (!document.querySelector(".modal.show")) {
            document
                .querySelectorAll(".modal-backdrop")
                .forEach((backdrop) => backdrop.remove());
            document.body.classList.remove("modal-open");
            document.body.style.removeProperty("overflow");
            document.body.style.removeProperty("padding-right");
        }
    }

    onAddQRCodeSetup() {
        if (this.formId) {
            this.showQRCodeSetupModal = true;
            this.isViewMode = false;
            this.isEditMode = false;
            this.qrCodeSetup = new QRCodeSetup();
            this.qrCodeSetupButton.nativeElement.click();
        }
    }

    private refreshTable() {
        if (this.formId > 0) {
            this.loading = true;
            this.qrCodeService.getAll().subscribe(
                (data) => {
                    this.qrCodeSetupRegister = [];
                    this.qrCodeSetupRegister = data;
                    this.onQRCodeSetupRegisterChange.emit(data);
                    this.loading = false;
                },
                (err) => {
                    this.loading = false;
                    this.asyncModalService.getModalResponse(err.error);
                },
                () => {
                    this.setQRCodeSetupRegister();
                    this.loading = false;
                }
            )
        }
    }

    private setQRCodeSetupRegister() {
        (this.qrCodeSetupRegister || []).forEach((entity) => {
            entity.entryNumber = entity.entryNumber;
            entity.name = entity.name;
            entity.lastUpdated = new DatePipe("en-US").transform(
                entity.modified?.toString() ?? entity.created?.toString(), "yyyy-MM-dd HH:mm"
            ),
                entity.lastUpdatedBy = (
                    entity.modifiedBy != "" ? entity.modifiedBy : entity.createdBy ?? ""
                ),
                entity.roleAccessType = (
                    entity.roleAccessType == QRCodeRoleAccessType[QRCodeRoleAccessType.RoleBased] ? "Role Based" : "Non Role Based"
                ),
                entity.status = entity.isActive ? Status.Active : Status.Inactive;
            entity.options = [
                {
                    value: Options.Edit,
                    controller: this,
                    click: (
                        controller: QRCodeRegister
                    ) => {
                        this.edit(controller, entity)
                    },
                },
                {
                    value: Options.View,
                    controller: this,
                    click: (
                        controller: QRCodeRegister
                    ) => {
                        this.view(controller, entity)
                    },
                },
                {
                    value: entity.status == "Active" ? Options.Deactivate : Options.Activate,
                    click: () => {
                        if (entity.status == "Active")
                            this.deactivate(
                                entity.entryNumber
                            );
                        else if (entity.status == "Inactive")
                            this.activate(
                                entity.entryNumber
                            );
                    }
                },
                {
                    value: Options.Remove,
                    click: async () => {
                        if (await this.confirmRemoveQRCodeSetup(entity.name))
                            this.delete(entity.entryNumber)
                    },
                },
            ] as ButtonOption[];
        });
    }

    private edit(controller: QRCodeRegister, row: QRCodeSetup) {
        if (row.entryNumber) {
            controller.showQRCodeSetupModal = true;
            controller.qrCodeSetup = QRCodeSetup.map(row);
            controller.isViewMode = false;
            controller.isEditMode = true;
            controller.qrCodeSetupButton.nativeElement.click();
        }
    }

    private view(controller: any, row: QRCodeSetup) {
        if (row.entryNumber) {
            controller.showQRCodeSetupModal = true;
            controller.qrCodeSetup = QRCodeSetup.map(row);
            controller.isViewMode = true;
            controller.isEditMode = false;
            controller.qrCodeSetupButton.nativeElement.click();
        }
    }

    private delete(entryNumber: number) {
        if (entryNumber) {
            this.qrCodeService
                .delete(entryNumber)
                .subscribe(
                    () => this.refreshTable(),
                    (err) => {
                        this.asyncModalService.getModalResponse(err.error);
                    }
                );
        }
    }

    private activate(entryNumber: number) {
        if (entryNumber) {
            this.qrCodeService
                .activate(entryNumber)
                .subscribe(
                    () => this.refreshTable(),
                    (err) => {
                        this.asyncModalService.getModalResponse(err.error);
                    }
                );
        }
    }

    private deactivate(entryNumber: number) {
        if (entryNumber) {
            this.qrCodeService
                .deactivate(entryNumber)
                .subscribe(
                    () => this.refreshTable(),
                    (err) => {
                        this.asyncModalService.getModalResponse(err.error);
                    }
                );
        }
    }

    private async confirmRemoveQRCodeSetup(qrCodeName) {
    return await this.asyncModalService.getModalResponse(
        "Are you sure you want to delete <b>" + qrCodeName + "</b> QR Code Setup?",
        [
            { label: "Yes", value: true },
            { label: "No", value: false },
        ]
    );
}
}
